import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { AdminSectionHeader, exportCsv, InlineError } from '@/components/admin/AdminShared';

export default function AdminQuestions() {
  const { data: questions = [], refetch, isLoading } = trpc.questions.listAll.useQuery();

  const [filter, setFilter] = useState<'all' | 'unanswered' | 'answered'>('unanswered');
  const visible = questions.filter(q =>
    filter === 'all'        ? true
    : filter === 'answered' ? q.reply != null
                            : q.reply == null,
  );

  const exportRows = () =>
    exportCsv(
      questions.map(q => ({
        ID:        q.id,
        Course:    q.course.title,
        User:      q.user.name,
        Phone:     q.user.phoneNumber,
        Question:  q.body,
        Reply:     q.reply ?? '',
        Asked:     new Date(q.createdAt).toLocaleString(),
        Answered:  q.repliedAt ? new Date(q.repliedAt).toLocaleString() : '',
      })),
      'mrpanadol_questions',
    );

  return (
    <div>
      <AdminSectionHeader title="Q&A inbox" count={questions.length} onExport={exportRows} />

      {/* Filter chips */}
      <div className="flex gap-2 mb-6">
        {(['unanswered', 'answered', 'all'] as const).map(f => (
          <button key={f}
            onClick={() => setFilter(f)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors
                        ${filter === f
                          ? 'bg-yellow-400/10 text-yellow-400 border-yellow-400/40'
                          : 'text-[#A1A1AA] border-white/10 hover:border-white/20'}`}>
            {f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-[#52525B] text-sm">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-[#52525B] text-sm">Nothing here.</p>
      ) : (
        <ul className="space-y-4">
          {visible.map(q => (
            <QuestionRow key={q.id} q={q as any} onChange={() => refetch()} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function QuestionRow({
  q, onChange,
}: {
  q: {
    id: number; body: string; reply: string | null; createdAt: string | Date;
    repliedAt: string | Date | null;
    course: { id: number; title: string };
    user:   { id: string; name: string; phoneNumber: string };
  };
  onChange: () => void;
}) {
  const [reply, setReply] = useState(q.reply ?? '');
  const replyMut = trpc.questions.reply.useMutation({ onSuccess: onChange });
  const del      = trpc.questions.delete.useMutation({ onSuccess: onChange });

  return (
    <li className="bg-[#0A0A0B] border border-[#27272A] rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-2">
        <div className="min-w-0">
          <p className="text-yellow-400 text-xs font-semibold uppercase tracking-wide">
            {q.course.title}
          </p>
          <p className="text-[#A1A1AA] text-xs">
            {q.user.name} · <span className="text-[#52525B]">{q.user.phoneNumber}</span>
            {' · '}
            <span className="text-[#52525B]">{new Date(q.createdAt).toLocaleString()}</span>
          </p>
        </div>
        <button
          onClick={() => del.mutate({ id: q.id })}
          className="text-[#52525B] hover:text-red-400 text-xs"
        >Delete</button>
      </div>

      <p className="text-[#E4E4E7] text-sm whitespace-pre-wrap mt-3">{q.body}</p>

      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-widest text-[#71717A] font-bold">
          {q.reply ? 'Your reply' : 'Reply'}
        </label>
        <textarea
          value={reply}
          onChange={e => setReply(e.target.value)}
          rows={3}
          className="w-full mt-1 bg-black border border-[#27272A] rounded-2xl px-4 py-3
                     text-sm text-white placeholder-[#52525B] outline-none
                     focus:border-yellow-400/40 resize-none"
          placeholder="Write a reply…"
        />
        {replyMut.error && <InlineError message={replyMut.error.message} />}
        <div className="flex justify-end mt-2">
          <button
            onClick={() => replyMut.mutate({ id: q.id, reply: reply.trim() })}
            disabled={replyMut.isPending || !reply.trim()}
            className="mp-btn mp-btn-primary px-5 py-2 text-sm rounded-xl"
          >
            {replyMut.isPending ? 'Saving…' : q.reply ? 'Update reply' : 'Send reply'}
          </button>
        </div>
      </div>
    </li>
  );
}
